import React, { useContext } from 'react';
import { Box, Flex, HStack } from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import styled, { ThemeContext } from 'styled-components';
import RadioButton from '../../../ui/advisory-flow/components/shared-components/RadioButton';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import FlowsQuestionText from '../../../ui/advisory-flow/components/FlowsQuestionText';
import FlowsBoxQuestionDescription from '../../../ui/advisory-flow/components/FlowsQuestionDescription';

interface Props {
  question: FlowsQuestion;
}

const RadioYesNoInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const { t } = useTranslation();

  const {
    field: { onBlur, value: isSelected },
    error,
  } = useFlowInputComponent({ question });

  const theme = useContext(ThemeContext);

  const handleOnNoClick = () => {
    if (isSelected === true || isSelected == null)
      onBlur({ name: question.id, value: false });
    else if (!question.rules.mandatory)
      onBlur({ name: question.id, value: null });
  };

  const handleOnYesClick = () => {
    if (isSelected === false || isSelected == null)
      onBlur({ name: question.id, value: true });
    else if (!question.rules.mandatory)
      onBlur({ name: question.id, value: null });
  };

  return (
    <Flex
      bg="white"
      w="100%"
      p="0.5"
      pl="8"
      pr="3"
      align={{ base: 'stretch', laptop: 'center' }}
      border="1px"
      borderColor={error?.message ? theme.status.error : 'gray.50'}
      borderRadius="6px"
      transitionDuration="0.2s"
      direction={{ base: 'column', laptop: 'row' }}
      boxShadow="base"
      transition="0.2s"
      {...(!question.rules.is_disabled && {
        _hover: { borderColor: 'gray.200' },
      })}
    >
      <Box flexGrow={1} mr="8" my="3">
        <HStack spacing="0" align="flex-start">
          <FlowsQuestionText whiteSpace="pre-wrap" question={question} />
        </HStack>
        {question.description && (
          <FlowsBoxQuestionDescription question={question} />
        )}
      </Box>
      <HStack alignSelf="center">
        <NoRadioButton
          isSelected={isSelected === false}
          onClick={handleOnNoClick}
          isDisabled={question.rules.is_disabled}
          {...{
            'data-test-id': `FLOW_INPUT-TYPE_RADIO_${question.id
              .toUpperCase()
              .replaceAll('.', '-')}_BUTTON-NO`,
          }}
        >
          {t('No')}
        </NoRadioButton>
        <YesRadioButton
          isSelected={isSelected === true}
          onClick={handleOnYesClick}
          isDisabled={question.rules.is_disabled}
          {...{
            'data-test-id': `FLOW_INPUT-TYPE_RADIO_${question.id
              .toUpperCase()
              .replaceAll('.', '-')}_BUTTON-YES`,
          }}
        >
          {t('Yes')}
        </YesRadioButton>
      </HStack>
    </Flex>
  );
};

const YesRadioButton = styled(RadioButton)`
  padding: 2.4rem;
  padding-left: 1.6rem;
`;

const NoRadioButton = styled(RadioButton)`
  padding: 2.4rem;
  padding-right: 1.6rem;
  margin-right: 1.6rem;
`;

export default RadioYesNoInputComponent;
